import '@/polyfills.js'

import '@/assets/styles/fonts.css'
import '@/assets/styles/global.scss'
import { newRelicReadyPromise } from '@/external_scripts'
import '@/plugins/elbwalker'
import '@/services/SiteConfig.js'
import {
  isChunkOrAssetFailure,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'

function setupChunkLoadErrorRecovery() {
  if (globalThis.window == undefined) return
  globalThis.window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkOrAssetFailure(event.reason)) return
    event.preventDefault()
    tryReloadForChunkError()
  })
}

setupChunkLoadErrorRecovery()

// Vue core's <Transition> leave hook forces a reflow via ownerDocument.body.offsetHeight
// without a null check (forceReflow in @vue/runtime-dom, still present in 3.5.35); body can
// be null in detached/headless contexts. Not reachable from app code, so suppress (VIP-12034).
// info is 'transition hook' in dev builds and an error-reference URL ending in the
// numeric code (TRANSITION_HOOK = 9) in production builds.
function isTransitionReflowFailure(error, info) {
  const isTransitionHook =
    info === 'transition hook' || String(info).endsWith('runtime-9')
  return (
    isTransitionHook &&
    error instanceof TypeError &&
    String(error.message).includes('offsetHeight')
  )
}

import cms from '@/plugins/cms.js'
import filters from '@/plugins/filters.js'
import {
  formatCurrency,
  setupTranslations,
  translate,
  install as translationPlugin
} from '@/plugins/i18n.js'
import lozad from '@/plugins/lozad.js'
import router from '@/router/index.js'
import { createPinia } from 'pinia'
import { createApp, markRaw, reactive, watch } from 'vue'

import App from '@/App.vue'
import vuetify from '@/plugins/vuetify.js'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { createHead, VueHeadMixin } from '@unhead/vue'
import { getComponentDefaults } from './plugins/vuetify'
import { HIGH_CONTRAST_TOGGLE } from './stores/accessibility'

const MOUNT_POINT = '#app'

;(async () => {
  await newRelicReadyPromise

  const app = createApp(App)

  // Vue app-wide error handler: reports to backend (→ New Relic, source: vue_error_handler), keeps console.error for devtools
  app.config.errorHandler = (error, instance, info) => {
    if (isChunkOrAssetFailure(error, undefined, info)) {
      // Suppress even when the once-per-session reload is already spent (VIP-12034)
      tryReloadForChunkError()
      console.error('Vue Error (chunk/asset, not reported):', error)
      return
    }

    if (isTransitionReflowFailure(error, info)) {
      console.error('Vue Error (transition reflow, not reported):', error)
      return
    }

    import('@/services/ApiController')
      .then(({ default: ApiController }) => {
        ApiController.logFrontendError(error, 'vue_error_handler', 'notice')
      })
      .catch(() => {})
    console.error('Vue Error:', error)
  }

  const pinia = createPinia()
  const i18n = await setupTranslations()
  const head = createHead()

  app.config.globalProperties.$globals = reactive({})

  pinia.use(({ store }) => {
    store.router = markRaw(router)
    store.translate = markRaw(translate)
    store.formatCurrency = markRaw(formatCurrency)
  })

  app.use(pinia)
  app.use(router)
  app.use(vuetify)
  app.use(translationPlugin, i18n)
  app.use(lozad)
  app.use(filters)
  app.use(cms)
  app.use(head)
  app.use(autoAnimatePlugin)
  app.mixin(VueHeadMixin)
  app.mount(MOUNT_POINT)

  if (localStorage.getItem(HIGH_CONTRAST_TOGGLE) === 'true') {
    vuetify.defaults.value = getComponentDefaults('vicampoThemeHighContrast')
  }

  watch(
    () => vuetify.theme.global.name.value,
    (newTheme) => {
      vuetify.defaults.value = getComponentDefaults(newTheme)
    }
  )
})()
